<template>
  <a-layout id="private-layout">
    <Sider :collapsed="collapsed" />
    <a-layout>
      <a-layout-header class="layout-header">
        <a-icon class="trigger" :type="collapsed ? 'menu-unfold' : 'menu-fold'" @click="() => (collapsed = !collapsed)" />
        <a-popover placement="bottomRight">
          <template slot="content">
            <div class="menu">
              <div class="menu-item" @click="onShowProfile()">
                My Account
                <div class="fs-12 color-prLgt">
                  <template v-if="impersonatedUser.id == loggedInUser.id">
                    <!-- Case where user is logged in as themselves -->
                    {{ loggedInUser.username }}
                  </template>
                  <template v-if="impersonatedUser.id != loggedInUser.id && impersonatedUser.id == loggedInUser.owner_id">
                    <!-- Case where user is logged in via ownerID = someone else (auto impersonate on login -->
                    <!-- in this case we show the logged in username as the user did not explicitly switch to another user -->
                    <!-- Technically, the user might be able to "switch user" back to the OID user too,
                    but that is not a usual case -->
                    {{ loggedInUser.username }}
                  </template>
                  <template v-if="impersonatedUser.id != loggedInUser.id && impersonatedUser.id != loggedInUser.owner_id">
                    <!-- Case where user switched to another user which is not their own user or the ownerID user -->
                    <!-- in this case we show both user names that the switch occurred -->
                    <div>Logged in as:</div>
                    <div>{{ loggedInUser.username }}</div>
                    <div>Viewing as:</div>
                    <div>{{ impersonatedUser.username }}</div>
                  </template>
                </div>
              </div>
              <div class="menu-item" @click="onManageCredentials()">
                Manage Credentials
              </div>
              <div class="menu-item" @click="onActivityLog()">
                Activity Log
              </div>
              <div class="menu-item" @click="onSwitchRole()">
                Switch Role
              </div>
              <div class="menu-item" @click="logout()">
                Logout
              </div>
            </div>
          </template>
          <a-avatar class="avatar" icon="user" />
        </a-popover>
      </a-layout-header>

      <a-layout class="layout-body">
        <Breadcrumb />
        <a-layout-content v-if="isLoadedUser" class="layout-content d-flex flex-column">
          <slot />
        </a-layout-content>
      </a-layout>
    </a-layout>
  </a-layout>
</template>

<script>
  import Breadcrumb from './components/Breadcrumb.vue';
  import Sider from './components/Sider.vue';

  import AuthenticationService from '@modules/Authentication/services/authentication.service';
  import JwtService from '@shared/services/jwt.service';
  import { UserDate } from '@shared/services/userDate.service';

  import moment from 'moment';

  export default {
    name: 'PrivateLayout',
    components: {
      Sider,
      Breadcrumb
    },
    data() {
      return {
        collapsed: true,
        isLoadedUser: false,
        loggedInUser: null,
        lastActiveClientTime: new Date(),
        lastTokenRefreshClientTime: null,
        // Renew every 5 mins
        tokenRefreshInterval: 5 * 60 * 1000,
        // In the last 6 minutes, we will immediately refresh if the user makes any activity
        tokenExpiryImmediateRefreshInterval: 6 * 60 * 1000,
        // In the last 2 minutes, we will show a warning that the session is expiring
        tokenExpiryWarningInterval: 2 * 60 * 1000,
        // Timers for setTimeout/setInterval
        tokenExpiryWarningTimer: null,
        tokenExpiryTimer: null,
        tokenRefreshIntervalTimer: null,
        tokenLastSyncTime: null,
        userActivityEvents: ['click', 'keyup'],
      };
    },
    computed: {
      impersonatedUser() {
        return this.$store.state.user.data;
      }
    },
    mounted() {
      this.startListeningForUserActivity();
      this.startListeningForStorageEvent();
      if (JwtService.getToken()) {
        setTimeout(() => {
          this.initUserData();
          this.startRefreshTokenTimer();
        }, 500);
      }
      this.collapsed = false;
    },
    beforeDestroy() {
      // Clean up event handlers
      this.stopListeningForUserActivity();
      this.stopListeningForStorageEvent();

      // Clean up timers
      if (this.tokenRefreshIntervalTimer) {
        clearInterval(this.tokenRefreshIntervalTimer);
      }

      if (this.tokenExpiryWarningTimer) {
        clearTimeout(this.tokenExpiryWarningTimer);
      }

      if (this.tokenExpiryTimer) {
        clearTimeout(this.tokenExpiryTimer);
      }
    },
    methods: {
      logout() {
        // Logout on the backend
        AuthenticationService.logout();

        // Clear the stored state
        this.$store.dispatch('CLEAR');
        this.$router.push('/login');
      },
      userInteractiveHandler() {
        this.lastActiveClientTime = new Date();

        // If going to expire soon, we renew immediately,
        // so that we renew the token before it actually expires on the server side
        // In this case we are reliant on the client time but this is just approximate.
        // Worst case is that the user's token doesn't get renewed on time, so they are logged out
        if (window.localStorage.getItem("tokenExpiryTime") && (new Date(window.localStorage.getItem("tokenExpiryTime")).getTime() - (new Date()).getTime() <= this.tokenExpiryImmediateRefreshInterval)) {
          this.refreshToken();
        }
      },
      storageUpdateHandler() {
        // If we receive a storage update event, we check if the tokenLastSyncTime has changed
        // If it is, we restart the expiry timers
        // This is to keep every open tab having the same timers
        if (new Date(window.localStorage.getItem("tokenLastSyncTime")) !== this.tokenLastSyncTime) {
          console.log("Storage event received, token last sync time does not match");
          const expiryTime = new Date(window.localStorage.getItem("tokenExpiryTime"));
          const currentTime = new Date(window.localStorage.getItem("tokenCurrentTime"));
          this.tokenLastSyncTime = new Date(window.localStorage.getItem("tokenLastSyncTime"));
          this.runTokenExpiryTimers(expiryTime, currentTime);
        }
      },
      startListeningForUserActivity() {
        for (let i = 0; i < this.userActivityEvents.length; i++) {
          window.addEventListener(this.userActivityEvents[i], this.userInteractiveHandler);
        }
      },
      stopListeningForUserActivity() {
        for (let i = 0; i < this.userActivityEvents.length; i++) {
          window.removeEventListener(this.userActivityEvents[i], this.userInteractiveHandler);
        }
      },
      startListeningForStorageEvent() {
        window.addEventListener("storage", this.storageUpdateHandler);
      },
      stopListeningForStorageEvent() {
        window.removeEventListener("storage", this.storageUpdateHandler);
      },
      async initUserData() {
        try {
          this.loggedInUser = await AuthenticationService.getUser();

          const data = await this.$store.dispatch('ENSUREUSER');
          const userTimezone = data && data.timezone;
          new UserDate().setUserTimezone(userTimezone);
          await this.refreshToken();
          this.isLoadedUser = true;

          // If user needs to change password, we send them to the change password page.
          // Note that this must be done AFTER the token is set
          if (this.loggedInUser.force_change_password_on_login) {
            this.$router.push('/account/manage-credentials');
          }
        } catch (error) {
          await this.$store.dispatch('FAILUSERLOAD');
          const accessUrl = this.$route.path;

          let shouldRedirect = true;
          if ('meta' in this.$route && 'isPublic' in this.$route.meta) {
            if (this.$route.meta.isPublic) {
              shouldRedirect = false;
            }
          }
          const urlsPublicShouldNotRedirect = ['/forgot-password', '/reset-password']
          if (shouldRedirect && !urlsPublicShouldNotRedirect.includes(accessUrl)) {
            this.$store.dispatch('CLEAR');
            JwtService.destroyToken();
            this.$router.push('/login');
          }
        }
      },
      startRefreshTokenTimer() {
        this.refreshToken();

        if (this.tokenRefreshIntervalTimer) {
          clearInterval(this.tokenRefreshIntervalTimer);
        }

        this.tokenRefreshIntervalTimer = setInterval(() => {
          this.refreshTokenIfUserIsActive();
        }, this.tokenRefreshInterval);
      },
      async refreshToken() {
        console.log("Refreshing token...");
        const result = await AuthenticationService.refreshToken();

        this.lastTokenRefreshClientTime = new Date();

        if (result) {
          const expiryTime = new Date(result['expiry']);
          window.localStorage.setItem("tokenExpiryTime", expiryTime.toJSON());

          // If we have the server_time in the API response (which should be there unless using older version of the API)
          // we use it instead of the client's time, just in case the client's time is wrong
          let currentTime = new Date();
          if ('server_time' in result) {
            currentTime = new Date(result['server_time']);
          }
          window.localStorage.setItem("tokenCurrentTime", currentTime.toJSON());

          await this.runTokenExpiryTimers(expiryTime, currentTime);
          // We also update the LocalStorage so that all tabs will receive a triggered event
          this.tokenLastSyncTime = new Date();
          window.localStorage.setItem("tokenLastSyncTime", (new Date()).toJSON());
        }
      },
      async refreshTokenIfUserIsActive() {
        if ((new Date()).getTime() - this.lastActiveClientTime.getTime() <= this.tokenRefreshInterval) {
          await this.refreshToken();
        }
      },
      async runTokenExpiryTimers(expiryTime, currentTime) {

        // Set timer to show session timeout warning
        // Also use the same timer to renew the token if it is expiring soon and user is active
        console.log("Setting warning timer:", expiryTime.getTime() - this.tokenExpiryWarningInterval - currentTime.getTime());

        if (this.tokenExpiryWarningTimer) {
          clearTimeout(this.tokenExpiryWarningTimer);
        }

        this.tokenExpiryWarningTimer = setTimeout(() => {
          console.log("Warning timer triggered");

          this.$notification.info({
            class: 'custom-notification info-message',
            message: 'Info',
            description: 'Your session is expiring soon at ' + moment(this.tokenExpiryTime).format('h:mm:ss a') + '. If there is no user activity, you will be logged out.',
            duration: this.tokenExpiryWarningInterval / 1000,
          });

        }, expiryTime.getTime() - this.tokenExpiryWarningInterval - currentTime.getTime());

        // Set timer to log user out (instead of leaving user on the same screen but getting 403 errors)
        console.log("Setting expiry timer:", expiryTime.getTime() - currentTime.getTime());

        if (this.tokenExpiryTimer) {
          clearTimeout(this.tokenExpiryTimer);
        }
        this.tokenExpiryTimer = setTimeout(() => {
          this.$notification.error({
            class: 'custom-notification error-message',
            message: 'Error',
            description: 'Your session has expired and you have been logged out.',
            duration: 0,
          });
          console.log("Session has expired and user has been logged out");
          this.logout();
        }, expiryTime.getTime() - currentTime.getTime());
      },
      onSwitchRole() {
        this.$router.push('/account/switch-role');
      },
      onShowProfile() {
        this.$router.push('/account/details');
      },
      onManageCredentials() {
        this.$router.push('/account/manage-credentials');
      },
      onActivityLog() {
        this.$router.push('/activity-log');
      }
    }
  };
</script>

<style lang="scss" scoped>
  #private-layout {
    height: 100%;
  }

  #private-layout .trigger {
    font-size: 18px;
    line-height: 64px;
    padding: 0 24px;
    transition: color 0.3s;
    cursor: pointer;
  }

  #private-layout .avatar {
    position: absolute;
    top: 15px;
    right: 15px;
    cursor: pointer;
  }

  .layout-header {
    background: #fff;
    padding: 0px;
  }

  .layout-body {
    padding: 0 20px 30px 20px;
  }

  .layout-header {
    background: white;
  }

  .layout-content {
    background: white;
    overflow: auto;
    min-height: 280px;
  }

  .breadcrumb {
    padding: 20px 0 20px 0;
  }

  .menu {
    padding: 0;
    margin: -16px;
    margin-bottom: -13px;
    width: 280px;
  }

  .menu-item {
    padding: 20px;
    text-align: left;
    cursor: pointer;
    color: $prDrk;

    &:not(:last-of-type) {
      border-bottom: solid 0.5px $border;
    }

    &:hover {
      background-color: #f7f7f7;
    }
  }
</style>
